/**
 * Inspection History
 *
 * What happened to one site inspection, in order: who scheduled it, which officer
 * it was handed to, every photograph with the time and place it was taken, and the
 * outcome that was recorded.
 *
 * Used by both surfaces that show an inspection — the Inspections page detail
 * dialog and the Work Profile's Inspections tab — so the two cannot drift apart.
 *
 * WHAT IT CANNOT SHOW. dswms_inspctn_lst_t is updated in place: an inspection
 * recorded Poor and later edited to Good stores only Good. The timeline therefore
 * carries ONE recorded/completed event holding the current outcome, not a replay of
 * every edit. The photographs and the assignment events are append-only and are the
 * genuinely per-visit part of the record.
 *
 * Photos are fetched as blobs rather than set as <img src>: the bytes come from the
 * API behind the JWT the axios interceptor adds, and a plain src would hit the
 * backend unauthenticated and render a broken image.
 */

import { useEffect, useState } from 'react';
import { ListSkeleton } from '@/components/base/Skeleton';
import { createPortal } from 'react-dom';
import {
  inspectionsApi, attachmentApi,
  type InspectionHistory as History, type InspectionEvent, type InspectionPhoto,
} from '../../services/wmsService';

/** Icon + tint per event kind, so the timeline is readable at a glance. */
const EVENT_STYLE: Record<string, { icon: string; ring: string; dot: string }> = {
  SCHEDULED:  { icon: 'ri-calendar-schedule-line', ring: 'border-gray-300',   dot: 'bg-gray-400' },
  ASSIGNED:   { icon: 'ri-user-received-line',     ring: 'border-blue-300',   dot: 'bg-blue-500' },
  REASSIGNED: { icon: 'ri-user-shared-line',       ring: 'border-amber-300',  dot: 'bg-amber-500' },
  PHOTO:      { icon: 'ri-camera-line',            ring: 'border-violet-300', dot: 'bg-violet-500' },
  RECORDED:   { icon: 'ri-clipboard-line',         ring: 'border-orange-300', dot: 'bg-orange-500' },
  COMPLETED:  { icon: 'ri-checkbox-circle-line',   ring: 'border-green-300',  dot: 'bg-green-500' },
};

const dash = (v: unknown) =>
  v === null || v === undefined || String(v).trim() === '' ? '—' : String(v);

/** A GPS point as a Google Maps link — the coordinate alone tells nobody anything. */
function GeoChip({ lat, lng, addr }: { lat?: number | null; lng?: number | null; addr?: string | null }) {
  if (lat == null || lng == null) return null;
  return (
    <a
      href={`https://www.google.com/maps?q=${lat},${lng}`}
      target="_blank" rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 hover:underline"
      title={addr || `${lat}, ${lng}`}
    >
      <i className="ri-map-pin-2-line"></i>
      <span className="truncate max-w-[220px]">
        {addr || `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`}
      </span>
    </a>
  );
}

interface Props {
  inspectionId: number | string;
  /** Drops the record summary block — for a surface that already shows those fields. */
  hideSummary?: boolean;
}

export default function InspectionHistory({ inspectionId, hideSummary }: Props) {
  const [history, setHistory] = useState<History | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  /** attchmnt_id -> object URL. Owned here; revoked when the component unmounts. */
  const [thumbs, setThumbs] = useState<Record<number, string>>({});
  /**
   * Photos whose bytes the server could not produce (GET /attachments/:id → 404
   * "Attachment bytes not found").
   *
   * The database is shared between servers but the FILES are not: attachments are
   * written to the local disk of whichever server took the upload (there is no S3),
   * so a photo shot against the live server has a row here and its bytes over
   * there. Without this the tile sat as a blank placeholder that looked identical
   * to one still loading, with only a red 404 in the console to explain it.
   */
  const [missing, setMissing] = useState<Record<number, true>>({});
  const [preview, setPreview] = useState<InspectionPhoto | null>(null);

  useEffect(() => {
    let cancelled = false;
    const urls: string[] = [];
    setLoading(true); setFailed(false); setHistory(null); setThumbs({}); setMissing({});

    (async () => {
      let h: History;
      try {
        h = await inspectionsApi.getHistory(inspectionId);
      } catch {
        if (!cancelled) { setFailed(true); setLoading(false); }
        return;
      }
      if (cancelled) return;
      setHistory(h);
      setLoading(false);

      // Photos load one at a time after the timeline is already on screen — the
      // history must not wait on the bytes, and one unreadable file must not cost
      // the rest of the gallery.
      for (const p of h.photos || []) {
        if (cancelled) break;
        try {
          const url = await attachmentApi.fetchBlobUrl(p.attchmnt_id);
          urls.push(url);
          if (cancelled) break;
          setThumbs((prev) => ({ ...prev, [p.attchmnt_id]: url }));
        } catch {
          // Say so on the tile rather than leaving a placeholder that reads as
          // "still loading" forever; the rest of the gallery is unaffected.
          if (!cancelled) setMissing((prev) => ({ ...prev, [p.attchmnt_id]: true }));
        }
      }
    })();

    return () => { cancelled = true; urls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [inspectionId]);

  if (loading) {
    return (
      <ListSkeleton rows={3} avatar={false} />
    );
  }

  if (failed || !history) {
    return (
      <div className="text-center py-6 text-xs text-gray-400">
        <i className="ri-error-warning-line text-lg block mb-1"></i>
        The inspection history could not be loaded.
      </div>
    );
  }

  const { inspection: insp, timeline, photos } = history;

  return (
    <div className="space-y-5">
      {/* Who scheduled it, who closed it — the row stores only user ids, so these
          names are the answer to "who completed the schedule". */}
      {!hideSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-lg border bg-gray-50 dark:bg-gray-700/40 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-gray-400">Scheduled Date</div>
            <div className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">{dash(insp.schdl_dt)}</div>
            <div className="text-[10px] text-gray-500 mt-0.5 truncate" title={insp.schdld_by_nm || ''}>
              by {dash(insp.schdld_by_nm)}
            </div>
          </div>
          <div className="rounded-lg border bg-gray-50 dark:bg-gray-700/40 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-gray-400">Completed Date</div>
            <div className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">{dash(insp.actl_dt)}</div>
            <div className="text-[10px] text-gray-500 mt-0.5 truncate">
              {/* "Completed by" is claimed only for an inspection that IS completed;
                  on any other status this person merely recorded against it. */}
              {insp.cmpltd_by_nm
                ? `by ${insp.cmpltd_by_nm}`
                : insp.rcrdd_by_nm ? `last recorded by ${insp.rcrdd_by_nm}` : 'not completed'}
            </div>
          </div>
          <div className="rounded-lg border bg-gray-50 dark:bg-gray-700/40 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-gray-400">Inspector</div>
            <div className="text-sm text-gray-900 dark:text-gray-100 mt-0.5 truncate" title={insp.inspctr_nm || ''}>
              {dash(insp.inspctr_nm)}
            </div>
            <div className="text-[10px] text-gray-500 mt-0.5 truncate">{dash(insp.inspctr_dsgn_nm)}</div>
          </div>
          <div className="rounded-lg border bg-gray-50 dark:bg-gray-700/40 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-gray-400">Location</div>
            <div className="text-sm text-gray-900 dark:text-gray-100 mt-0.5 break-words">{dash(insp.lctn_tx)}</div>
          </div>
        </div>
      )}

      {/* Photographs */}
      <div>
        <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2 pb-1 border-b flex items-center gap-2">
          <i className="ri-image-line"></i> Inspection Photos
          <span className="text-gray-400 font-normal normal-case">({photos.length})</span>
        </div>
        {photos.length === 0 ? (
          <p className="text-xs text-gray-400 py-3">
            No photographs are linked to this inspection. Photos captured in the field app
            against this inspection appear here with the time and place they were taken.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {photos.map((p) => (
              <button
                key={p.cmpnt_img_id}
                type="button"
                onClick={() => setPreview(p)}
                className="text-left group rounded-lg border overflow-hidden bg-gray-50 dark:bg-gray-700/40 hover:shadow-md transition-shadow"
              >
                <div className="aspect-[4/3] bg-gray-200 dark:bg-gray-600 flex items-center justify-center overflow-hidden">
                  {thumbs[p.attchmnt_id] ? (
                    <img src={thumbs[p.attchmnt_id]} alt={p.cptn_tx || p.file_nm || 'Inspection photo'}
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform" />
                  ) : missing[p.attchmnt_id] ? (
                    <div className="text-center px-2">
                      <i className="ri-image-off-line text-2xl text-gray-400 block"></i>
                      <span className="text-[10px] text-gray-500 leading-tight block mt-1">
                        Image not on this server
                      </span>
                    </div>
                  ) : (
                    <i className="ri-image-line text-2xl text-gray-400"></i>
                  )}
                </div>
                <div className="px-2 py-1.5 space-y-0.5">
                  <div className="text-[11px] text-gray-800 dark:text-gray-200 truncate" title={p.cptn_tx || ''}>
                    {p.cptn_tx || <span className="text-gray-400">No caption</span>}
                  </div>
                  <div className="text-[10px] text-gray-500 flex items-center gap-1">
                    <i className="ri-time-line"></i>{dash(p.captured_at_tx)}
                  </div>
                  {p.uploaded_by_nm && (
                    <div className="text-[10px] text-gray-500 truncate">
                      <i className="ri-user-line"></i> {p.uploaded_by_nm}
                    </div>
                  )}
                  <GeoChip lat={p.lat} lng={p.lng} addr={p.addr_tx} />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Observations as written on site */}
      <div>
        <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2 pb-1 border-b flex items-center gap-2">
          <i className="ri-chat-quote-line"></i> Observations
        </div>
        <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{dash(insp.rmrks_tx)}</p>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <div className="px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700/40 border">
            <div className="text-lg font-bold text-gray-900 dark:text-gray-100 leading-none">{Number(insp.fndng_ct || 0)}</div>
            <div className="text-[10px] text-gray-500 mt-1">Total findings</div>
          </div>
          <div className={`px-3 py-2 rounded-lg border ${Number(insp.crtcl_fndng_ct || 0) > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 dark:bg-gray-700/40'}`}>
            <div className={`text-lg font-bold leading-none ${Number(insp.crtcl_fndng_ct || 0) > 0 ? 'text-red-600' : 'text-gray-900 dark:text-gray-100'}`}>
              {Number(insp.crtcl_fndng_ct || 0)}
            </div>
            <div className="text-[10px] text-gray-500 mt-1">Critical</div>
          </div>
        </div>
      </div>

      {/* The timeline itself */}
      <div>
        <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2 pb-1 border-b flex items-center gap-2">
          <i className="ri-history-line"></i> History
        </div>
        <ol className="relative border-l border-gray-200 dark:border-gray-600 ml-2 space-y-4 py-1">
          {timeline.map((e: InspectionEvent, i: number) => {
            const st = EVENT_STYLE[e.event_cd] || EVENT_STYLE.SCHEDULED;
            return (
              <li key={`${e.event_cd}-${e.event_ts}-${i}`} className="ml-5">
                <span className={`absolute -left-[7px] flex items-center justify-center w-3.5 h-3.5 rounded-full ${st.dot} ring-4 ring-white dark:ring-gray-800`} />
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
                    <i className={`${st.icon} mr-1 text-gray-500`}></i>{e.event_nm}
                  </span>
                  <span className="text-[11px] text-gray-400">{dash(e.event_ts_tx)}</span>
                  {e.actor_nm && (
                    <span className="text-[11px] text-gray-500">
                      · {e.actor_nm}{e.actor_dsgn_nm ? `, ${e.actor_dsgn_nm}` : ''}
                    </span>
                  )}
                </div>
                {e.dtl_tx && (
                  <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 break-words">{e.dtl_tx}</p>
                )}
                {e.event_cd === 'PHOTO' && <GeoChip lat={e.lat} lng={e.lng} addr={e.addr_tx} />}
                {e.rmrks_tx && (
                  <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 pl-2 border-l-2 border-gray-200 italic whitespace-pre-wrap">
                    {e.rmrks_tx}
                  </p>
                )}
              </li>
            );
          })}
        </ol>
      </div>

      {/* Full-size photo, with everything the field app captured alongside it */}
      {preview && createPortal(
        <div className="fixed inset-0 z-[10050] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}>
          <div className="relative max-w-5xl w-full max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPreview(null)}
              className="absolute -top-9 right-0 text-white hover:text-gray-300">
              <i className="ri-close-line text-3xl"></i>
            </button>
            {thumbs[preview.attchmnt_id] ? (
              <img src={thumbs[preview.attchmnt_id]} alt={preview.cptn_tx || 'Inspection photo'}
                className="w-full h-auto max-h-[72vh] object-contain rounded-lg bg-black/30" />
            ) : (
              <div className="h-64 flex flex-col items-center justify-center text-white/70 gap-2">
                <i className={`${missing[preview.attchmnt_id] ? 'ri-image-off-line' : 'ri-image-line'} text-4xl`}></i>
                {missing[preview.attchmnt_id] && (
                  <p className="text-xs text-center max-w-sm">
                    The file is recorded but its bytes are not on this server — attachments
                    are stored on the disk of whichever server received the upload.
                  </p>
                )}
              </div>
            )}
            <div className="mt-3 bg-white dark:bg-gray-800 rounded-lg px-4 py-3 text-xs space-y-1">
              <div className="font-medium text-gray-900 dark:text-gray-100">
                {preview.cptn_tx || preview.file_nm || 'Inspection photo'}
              </div>
              <div className="text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                <span><i className="ri-time-line mr-1"></i>{dash(preview.captured_at_tx)}</span>
                {preview.uploaded_by_nm && <span><i className="ri-user-line mr-1"></i>{preview.uploaded_by_nm}</span>}
                {preview.src_cd && <span><i className="ri-camera-line mr-1"></i>{preview.src_cd}</span>}
                {preview.accuracy != null && <span>±{Number(preview.accuracy).toFixed(0)} m</span>}
              </div>
              <GeoChip lat={preview.lat} lng={preview.lng} addr={preview.addr_tx} />
            </div>
          </div>
        </div>, document.body)}
    </div>
  );
}
